// Page navigation over the unified renderer: derived pages, a progress bar,
// Back/Next with per-page validation, and automatic skipping of pages whose
// content is entirely hidden. The public form and the builder's test mode both
// drive paging through this hook, so they cannot disagree about it.
//
// It also owns the validation errors on screen. A failed Next or submit hands
// the pager a list; the pager maps it per control for the renderer, lists it
// in a summary above the buttons, and moves the keyboard to the first one —
// jumping to that page first when the error is not on the one being viewed.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { FormConfig, FormSection } from '../shared/types';
import { FormState, PublishedDef } from './model';
import { FormEval, evaluateForm } from './evaluate';
import { derivePages, pageFieldKeys, pageHasContent, walkPages } from './pages';
import { FieldErrors, ValidationError, errorLine, formatValidationErrors, toFieldErrors, validateForm } from './payload';
import { afterPaint, focusErrorPath, focusFirstError } from './focus';
import { STRINGS as S } from './strings';

export interface PagerState {
  /** false = single-page form; render it exactly as before */
  paged: boolean;
  ev: FormEval;
  /** the sections of the current page (all sections when not paged) */
  sections: FormSection[];
  /** 1-based position among currently visible pages */
  position: number;
  total: number;
  title: string;
  atFirst: boolean;
  atLast: boolean;
  /** the sections on pages a firing `goto` jumps over — the submit path neither checks nor sends them */
  skippedSections: Set<string>;
  /** validate the current page; advance and return true, or show the errors */
  next: () => boolean;
  back: () => void;
  /** back to page 1 (e.g. "Submit another") */
  reset: () => void;
  /** the errors on screen as one sentence — what the page showed before they landed on fields */
  pageError: string;
  /** raw index of the current page — what a saved draft remembers */
  index: number;
  /** jump straight to a page index (draft resume); no validation on the way */
  goTo: (i: number) => void;
  /** every error on screen, in document order — the summary's list */
  errorList: ValidationError[];
  /** the same, per control, for the renderer */
  errors: FieldErrors;
  /** a field-less line at the top of the summary (a server refusal, unconfirmed carried answers) */
  errorMessage: string;
  /**
   * Put these errors on screen: map them to controls, jump to the page the
   * first one is on, and focus it once painted.
   */
  showErrors: (list: ValidationError[], message?: string) => void;
  /** an answer changed — drop the error for that control */
  clearError: (path: string) => void;
  clearErrors: () => void;
  /** a control reported its own problem (a refused file): add it, replacing any error on that path */
  setError: (error: ValidationError) => void;
  /** what a summary item does: bring that control into view and focus it */
  focusError: (path: string) => void;
  /**
   * D15 (R9): the form asked for the summary at the TOP of the page. Then
   * `ErrorSummaryTop` renders it there, it takes the keyboard when it appears,
   * and `PagerControls` draws nothing above the buttons. Off (the default),
   * every render is exactly what it was.
   */
  summaryAtTop: boolean;
}

export interface PagerOptions {
  /** `settings.errorSummary`: the summary of what to fix sits at the top of the page (D15) */
  errorSummary?: boolean;
}

/** The top summary, when the page has one — what a failed attempt focuses. */
function focusTopSummary(): boolean {
  const el = document.querySelector('.of-error-summary-top') as HTMLElement | null;
  if (!el) return false;
  try { el.scrollIntoView?.({ block: 'start', behavior: 'smooth' }); } catch { /* jsdom */ }
  try { el.focus({ preventScroll: true }); } catch { try { el.focus(); } catch { /* not focusable */ } }
  return document.activeElement === el;
}

export function useFormPager(def: PublishedDef, state: FormState, cfg: FormConfig, opts: PagerOptions = {}): PagerState {
  const summaryAtTop = opts.errorSummary === true;
  const ev = useMemo(() => evaluateForm(def, state, cfg), [def, state, cfg]);
  const pages = useMemo(() => derivePages(def.layout?.sections || []), [def]);
  const [idx, setIdx] = useState(0);
  const [errorList, setErrorList] = useState<ValidationError[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  // Bumped when errors should take the keyboard, and when a page turn should
  // hand it to the new page's heading. Effects, not calls: both wait for the
  // render that puts the target in the DOM.
  const [focusTick, setFocusTick] = useState(0);
  const [headingTick, setHeadingTick] = useState(0);
  const mounted = useRef(false);

  useEffect(() => { setIdx(0); setErrorList([]); setErrorMessage(''); }, [def]);

  useEffect(() => {
    if (!focusTick) return;
    // With the summary at the top (D15) the box itself takes the keyboard, so
    // a screen reader hears the count and the list before any one field; the
    // links in it then take the person to each control.
    afterPaint(() => { if (summaryAtTop && focusTopSummary()) return; focusFirstError(); });
  }, [focusTick]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    if (!headingTick) return;
    afterPaint(() => {
      const heading = document.querySelector('.of-pager-label[tabindex]') as HTMLElement | null;
      try { heading?.focus({ preventScroll: true }); } catch { /* jsdom */ }
    });
  }, [headingTick]);

  // Pages with nothing to show right now are invisible to navigation. The page
  // being viewed stays put even if an answer empties it mid-edit — Next/Back
  // simply walk to the nearest page with content.
  const visible = useMemo(
    () => pages.map((_, i) => i).filter((i) => pageHasContent(pages[i], def, ev, cfg)),
    [pages, def, ev, cfg]
  );
  // The route a `goto` bends: Next lands on the page a firing rule names and
  // Back retraces the jump. A page the route no longer passes through (an
  // answer on it just made a jump fire) walks the plain order instead, so
  // the respondent is never stranded on a page with no way off it.
  const walk = useMemo(() => walkPages(pages, def, ev, cfg), [pages, def, ev, cfg]);
  const route = walk.path.includes(idx) ? walk.path : visible;
  const pos = route.indexOf(idx);
  const position = pos >= 0 ? pos + 1 : 1;

  const clearErrors = () => { setErrorList([]); setErrorMessage(''); };

  const showErrors = (list: ValidationError[], message = '') => {
    setErrorList(list);
    setErrorMessage(message);
    if (!list.length && !message) return;
    // The first error may sit on another page (a server-side refusal after
    // the last page validated clean): go there, then focus it.
    if (list.length && pages.length > 1) {
      const at = pages.findIndex((p) => pageFieldKeys(p, def).has(list[0].key));
      if (at >= 0 && at !== idx) setIdx(at);
    }
    setFocusTick((t) => t + 1);
  };

  const next = (): boolean => {
    const page = pages[idx];
    const keys = pageFieldKeys(page, def);
    const errors = validateForm(def, state, ev, cfg).filter((e) => keys.has(e.key));
    if (errors.length) { showErrors(errors); return false; }
    clearErrors();
    const after = route.filter((i) => i > idx);
    if (after.length) { setIdx(after[0]); turnPage(); return true; }
    return false;
  };

  const back = () => {
    clearErrors();
    const before = route.filter((i) => i < idx);
    if (before.length) { setIdx(before[before.length - 1]); turnPage(); }
  };

  const turnPage = () => { scrollTop(); setHeadingTick((t) => t + 1); };

  const errors = useMemo(() => toFieldErrors(errorList), [errorList]);

  return {
    paged: pages.length > 1,
    ev,
    sections: pages[idx]?.sections || [],
    position,
    total: route.length || 1,
    title: pages[idx]?.title || '',
    atFirst: pos <= 0,
    atLast: pos === route.length - 1 || pos === -1,
    skippedSections: walk.skippedSections,
    next,
    back,
    reset: () => { setIdx(0); clearErrors(); },
    pageError: errorList.length ? formatValidationErrors(errorList) : errorMessage,
    index: idx,
    goTo: (i: number) => {
      if (Number.isInteger(i) && i >= 0 && i < pages.length) { setIdx(i); clearErrors(); }
    },
    errorList,
    errors,
    errorMessage,
    showErrors,
    clearError: (path: string) => setErrorList((l) => (l.some((e) => e.path === path) ? l.filter((e) => e.path !== path) : l)),
    setError: (error: ValidationError) => setErrorList((l) => [...l.filter((e) => e.path !== error.path), error]),
    clearErrors,
    focusError: (path: string) => { focusErrorPath(path); },
    summaryAtTop,
  };
}

function scrollTop() {
  try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* jsdom */ }
}

/** The step indicator above a paged form. Its label takes focus on a page turn. */
export function PagerProgress({ pager }: { pager: PagerState }) {
  if (!pager.paged) return null;
  return (
    <div className="of-pager-progress">
      <div className="of-pager-label" tabIndex={-1}>
        {S.step(pager.position, pager.total)}{pager.title ? ` — ${pager.title}` : ''}
      </div>
      <div className="of-pager-track">
        <div className="of-pager-fill" style={{ width: `${(pager.position / pager.total) * 100}%` }} />
      </div>
    </div>
  );
}

/**
 * Every error on screen, as an index: one line each, and each line a button
 * that takes the keyboard to the control. Announced as a whole when it
 * appears, so a screen reader hears the count and the list, not only the
 * first field's own alert.
 */
export function ErrorSummary({ pager, top = false }: { pager: PagerState; top?: boolean }) {
  const { errorList, errorMessage } = pager;
  // The validator walks the catalog; the respondent reads the page. The list
  // is put in reading order by where each control actually is — which is only
  // knowable once the renderer has stamped the paths, so the summary waits
  // one paint (rendering nothing, so it is announced once, sorted).
  const [sorted, setSorted] = useState<{ of: ValidationError[]; list: ValidationError[] } | null>(null);
  useLayoutEffect(() => {
    if (!errorList.length) return;
    const rank = new Map<string, number>();
    document.querySelectorAll('[data-error-path]').forEach((el, i) => {
      const p = el.getAttribute('data-error-path');
      if (p && !rank.has(p)) rank.set(p, i);
    });
    const at = (e: ValidationError) => rank.get(e.path) ?? Number.MAX_SAFE_INTEGER;
    setSorted({ of: errorList, list: errorList.slice().sort((a, b) => at(a) - at(b)) });
  }, [errorList]);
  if (!errorList.length && !errorMessage) return null;
  if (errorList.length && sorted?.of !== errorList) return null;
  const list = sorted?.of === errorList ? sorted.list : [];
  // The top variant (D15) is the same box drawn where the page starts: it
  // can take focus, its heading counts what is left to fix, and each item
  // names the widget item its field sits in ("Guests 2 › Email"). The bottom
  // variant renders exactly the attributes and text it always did.
  return (
    <div className={top ? 'of-error-summary of-error-summary-top' : 'of-error-summary'} role="alert" aria-live="assertive" tabIndex={top ? -1 : undefined}>
      <p className="of-error-summary-title">{errorMessage || (top ? S.errorHeadingTop(errorList.length) : S.errorHeading(errorList.length))}</p>
      {list.length > 0 && (
        <ul>
          {list.map((e) => (
            <li key={e.path}>
              <button type="button" onClick={() => pager.focusError(e.path)}>{top ? errorItem(e) : errorLine(e)}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One line of the top summary: the item's place, then the field, then what is wrong. */
function errorItem(e: ValidationError): string {
  const name = e.where ? `${e.where} › ${e.label}` : e.label;
  return e.message ? `${name} — ${e.message}` : name;
}

/**
 * The summary at the top of the page (D15), rendered by the page right under
 * its progress bar. Nothing at all unless the form opted in, so a form that
 * did not is byte-identical to what it was.
 */
export function ErrorSummaryTop({ pager }: { pager: PagerState }) {
  if (!pager.summaryAtTop) return null;
  return <ErrorSummary pager={pager} top />;
}

/** Back/Next row under a paged form. The submit control renders only at the end. */
export function PagerControls({ pager, children }: { pager: PagerState; children?: React.ReactNode }) {
  // With the summary at the top (D15) nothing is drawn here; the box is
  // already on the page above the fields.
  if (!pager.paged) return <>{!pager.summaryAtTop && <ErrorSummary pager={pager} />}{children}</>;
  return (
    <>
      {!pager.summaryAtTop && <ErrorSummary pager={pager} />}
      {/*
        On a phone this row is the sticky bar of §4C/C2: the step number, an
        arrow-only Back and Next filling the rest (form.css). Both halves of
        Back are in the DOM at every width and one of them is hidden, so the
        markup does not have to ask a media query what it is — and the
        button's accessible name stays "Back" either way.
      */}
      <div className="of-pager-controls">
        <span className="of-pager-step" aria-hidden="true">{pager.position} / {pager.total}</span>
        {!pager.atFirst && (
          <button type="button" className="of-btn of-btn-back" aria-label={S.back} onClick={pager.back}>
            <span className="of-back-label">{S.back}</span>
            <span className="of-back-icon" aria-hidden="true">←</span>
          </button>
        )}
        {pager.atLast
          ? <div style={{ flex: 1 }}>{children}</div>
          : <button type="button" className="of-btn" style={{ flex: 1 }} onClick={pager.next}>{S.next}</button>}
      </div>
    </>
  );
}
